// src/pages/DepositReconcile/DepositReconcile.jsx
import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  TextField,
  IconButton,
  Tooltip,
  Stack,
  LinearProgress,
  Alert,
  AlertTitle,
} from '@mui/material';
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarFilterButton,
} from '@mui/x-data-grid';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import {
  useGetReconcileSummaryQuery,
  useGetReconcileDepositsQuery,
} from '../../store/api/depositReconcileApi';

/* What each reconcile outcome means, in dry run and live mode. */
const OUTCOMES = {
  // Dry run
  would_credit:         { label: 'Would credit',            color: 'warning', help: 'ZynlePay confirms the player paid this amount. Live mode would add it to their wallet.' },
  would_skip_duplicate: { label: 'Would skip (duplicate)',  color: 'info',    help: 'Another row for the same session was already credited. Live mode would pay nothing.' },
  would_flag_mismatch:  { label: 'Would flag (amount)',     color: 'error',   help: 'ZynlePay reports a different or missing amount. Live mode would NOT credit; review manually.' },
  dry_failed:           { label: 'Would mark failed',       color: 'default', help: 'ZynlePay says the payment failed. No money moved.' },
  dry_not_found:        { label: 'Not at ZynlePay',         color: 'default', help: 'The prompt never reached ZynlePay. Live mode marks it failed after 30 minutes. No money moved.' },
  dry_open:             { label: 'Still pending',           color: 'default', help: 'ZynlePay has no final answer yet. Checked again every 5 minutes.' },
  dry_error:            { label: 'Check error',             color: 'error',   help: 'Could not reach the ZynlePay proxy. Retried every 5 minutes.' },
  // Live
  credited:             { label: 'Credited',                color: 'success', help: 'Payment confirmed by ZynlePay and added to the wallet.' },
  duplicate_session:    { label: 'Duplicate, not paid',     color: 'info',    help: 'Session already credited on another row. Marked failed, nothing paid.' },
  amount_mismatch:      { label: 'Amount mismatch',         color: 'error',   help: 'ZynlePay amount differs from the deposit. Not credited; review manually.' },
  failed:               { label: 'Marked failed',           color: 'default', help: 'ZynlePay says failed, or it never reached ZynlePay. No money moved.' },
  not_found:            { label: 'Not at ZynlePay (<30m)',  color: 'default', help: 'Not found at ZynlePay yet; will be marked failed after 30 minutes.' },
  open:                 { label: 'Still pending',           color: 'default', help: 'ZynlePay has no final answer yet.' },
  error:                { label: 'Check error',             color: 'error',   help: 'Could not reach the ZynlePay proxy.' },
};

const outcomeMeta = (key) => OUTCOMES[key] || { label: key, color: 'default', help: '' };

const fmtK = (n) => `K${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const fmtDate = (v) => (v ? new Date(v).toLocaleString() : '-');

// Custom Toolbar
const CustomToolbar = () => (
  <GridToolbarContainer>
    <GridToolbarFilterButton />
    <GridToolbarExport />
  </GridToolbarContainer>
);

// Outcome card; click to filter the table
const OutcomeCard = ({ outcome, count, amount, players, withBetPick, selected, onClick }) => {
  const meta = outcomeMeta(outcome);
  return (
    <Card sx={{ height: '100%', outline: selected ? 2 : 0, outlineColor: 'primary.main' }}>
      <CardActionArea onClick={onClick} sx={{ height: '100%' }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
            <Chip size="small" label={meta.label} color={meta.color} />
            <Typography variant="caption" color="text.secondary">
              {players?.toLocaleString()} players
            </Typography>
          </Stack>
          <Typography variant="h5" fontWeight="bold">
            {fmtK(amount)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {count?.toLocaleString()} deposits
            {withBetPick ? ` · ${withBetPick} with USSD bet pick` : ''}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" mt={1}>
            {meta.help}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

const DepositReconcile = () => {
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [filters, setFilters] = useState({ outcome: '', search: '' });

  const { data: summaryData, isLoading: summaryLoading, refetch: refetchSummary } = useGetReconcileSummaryQuery(
    undefined,
    { pollingInterval: 60_000 }
  );
  const { data: listData, isLoading: listLoading, isFetching: listFetching, refetch: refetchList } = useGetReconcileDepositsQuery({
    page: paginationModel.page + 1,
    limit: paginationModel.pageSize,
    ...(filters.outcome ? { outcome: filters.outcome } : {}),
    ...(filters.search ? { search: filters.search } : {}),
  });

  const summary = summaryData?.data || {};
  const outcomes = summary.outcomes || [];
  const rows = listData?.data?.deposits || [];
  const pagination = listData?.data?.pagination || { total: 0 };

  const wouldCredit = outcomes.find((o) => o.outcome === 'would_credit');
  const credited = outcomes.find((o) => o.outcome === 'credited');

  const handleFilterChange = useCallback((field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);

  const handleRefresh = () => {
    refetchSummary();
    refetchList();
  };

  const columns = [
    {
      field: 'outcome',
      headerName: 'Result',
      width: 190,
      renderCell: (params) => {
        const meta = outcomeMeta(params.value);
        return (
          <Tooltip title={meta.help}>
            <Chip size="small" label={meta.label} color={meta.color} />
          </Tooltip>
        );
      },
    },
    {
      field: 'phone',
      headerName: 'Phone',
      width: 140,
      renderCell: (params) => (
        <Typography variant="body2" fontFamily="monospace">
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 100,
      type: 'number',
      valueFormatter: (value) => fmtK(value),
    },
    { field: 'status', headerName: 'Deposit status', width: 120 },
    {
      field: 'betSide',
      headerName: 'USSD bet',
      width: 110,
      valueGetter: (value, row) => (row.autoBetStatus ? `${value || ''} (${row.autoBetStatus})` : value || '-'),
    },
    { field: 'provider', headerName: 'Network', width: 130 },
    {
      field: 'createdAt',
      headerName: 'Deposited',
      width: 170,
      valueFormatter: (value) => fmtDate(value),
    },
    {
      field: 'lastCheckedAt',
      headerName: 'Last checked',
      width: 170,
      valueFormatter: (value) => fmtDate(value),
    },
    {
      field: 'duplicateRow',
      headerName: 'Duplicate row',
      width: 110,
      valueGetter: (value, row) => (row.duplicateOf ? `of ${row.duplicateOf}` : value ? 'yes' : ''),
    },
    { field: 'error', headerName: 'Error / note', width: 200, valueGetter: (value, row) => value || row.note || '' },
    {
      field: 'sessionId',
      headerName: 'Session',
      width: 180,
      renderCell: (params) => (
        <Typography variant="body2" fontFamily="monospace">
          {params.value}
        </Typography>
      ),
    },
    { field: 'depositId', headerName: 'Deposit ID', width: 200 },
  ];

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Deposit Reconcile
          </Typography>
          <Typography variant="body2" color="text.secondary">
            ZynlePay deposits stuck in PENDING (last {summary.windowDays || 7} days), checked against ZynlePay every minute
          </Typography>
        </Box>
        <Tooltip title="Refresh Data">
          <IconButton onClick={handleRefresh} color="primary">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      {summaryLoading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Mode banner */}
      {summaryData && (
        summary.dryRun ? (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <AlertTitle>Dry run: no money has moved</AlertTitle>
            DEPOSIT_RECONCILE_DRY_RUN=true. This page shows what the reconciler <b>would</b> do.
            Going live would credit <b>{fmtK(wouldCredit?.amount)}</b> to{' '}
            <b>{(wouldCredit?.players || 0).toLocaleString()}</b> players ({(wouldCredit?.count || 0).toLocaleString()} deposits).
            Before going live, check the “Would credit” list (Export → CSV) for players support already refunded by hand.
          </Alert>
        ) : (
          <Alert severity="success" sx={{ mb: 3 }}>
            <AlertTitle>Live: confirmed payments are credited automatically</AlertTitle>
            Credited so far: <b>{fmtK(credited?.amount)}</b> ({(credited?.count || 0).toLocaleString()} deposits).
          </Alert>
        )
      )}

      {/* Outcome cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Chip size="small" label="Not checked yet" sx={{ mb: 1 }} />
              <Typography variant="h5" fontWeight="bold">
                {fmtK(summary.unchecked?.amount)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {(summary.unchecked?.count || 0).toLocaleString()} stuck deposits waiting (checked 50 per minute)
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        {outcomes.map((o) => (
          <Grid item xs={12} sm={6} md={3} key={o.outcome}>
            <OutcomeCard
              {...o}
              selected={filters.outcome === o.outcome}
              onClick={() => handleFilterChange('outcome', filters.outcome === o.outcome ? '' : o.outcome)}
            />
          </Grid>
        ))}
        {(summary.autoBets || []).length > 0 && (
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Chip size="small" label="USSD auto-bets after late credit" color="secondary" sx={{ mb: 1 }} />
                {summary.autoBets.map((b) => (
                  <Typography key={b.status} variant="body2">
                    {b.status}: <b>{b.count}</b> ({fmtK(b.amount)})
                  </Typography>
                ))}
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <TextField
            size="small"
            placeholder="Search phone..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            sx={{ minWidth: 200 }}
          />
          {filters.outcome && (
            <Chip
              label={`Showing: ${outcomeMeta(filters.outcome).label}`}
              onDelete={() => handleFilterChange('outcome', '')}
              color="primary"
              variant="outlined"
            />
          )}
        </Stack>
      </Paper>

      {/* Table */}
      <Paper sx={{ height: 560 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={listLoading || listFetching}
          rowCount={pagination.total}
          pageSizeOptions={[25, 50, 100, 200]}
          paginationModel={paginationModel}
          paginationMode="server"
          onPaginationModelChange={setPaginationModel}
          slots={{ toolbar: CustomToolbar }}
          disableRowSelectionOnClick
        />
      </Paper>
    </Box>
  );
};

export default DepositReconcile;
